import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import BottomNavigation from '../../components/feature/BottomNavigation';
import { useAuth } from '../../contexts/AuthContext';
import FamilyService from '../../services/familyService';
import { adherenceService } from '../../services/adherenceService';
import toast from '../../utils/toast';
import IconSelectorModal from '../../components/feature/IconSelectorModal';
import { logger } from '../../utils/logger';
import { SubscriptionManager } from '../../components/payment/SubscriptionManager';
import { featureGateService } from '../../services/featureGateService';
import { UpgradeModal } from '../../components/payment/UpgradeModal';
import { useSubscription } from '../../hooks/useSubscription';

interface Member {
  id: string;
  name: string;
  birthDate: string;
  gender: 'male' | 'female';
  avatar: string;
  status: 'excellent' | 'good' | 'warning' | 'critical';
}

interface UserProfile {
  id: string;
  name: string;
  email: string;
  familyName: string;
  createdAt: string;
}
const isImageAvatar = (avatar?: string) =>
  typeof avatar === 'string' &&
  (avatar.startsWith('/Imagens/') || avatar.startsWith('http') || avatar.startsWith('data:'));

export default function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, deleteAccount } = useAuth();
  const { subscription } = useSubscription();
  const familyService = useMemo(() => FamilyService.getInstance(), []);
  const initialTab = useMemo<'profile' | 'payment' | 'family'>(() => {
    const params = new URLSearchParams(location.search);
    const t = (params.get('tab') || '').toLowerCase();
    return t === 'payment' || t === 'family' ? (t as 'payment' | 'family') : 'profile';
  }, [location.search]);
  const [activeTab, setActiveTab] = useState<'profile' | 'payment' | 'family'>(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showIconModal, setShowIconModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [loading, setLoading] = useState(true);
  const [averageAdherence, setAverageAdherence] = useState(0);
  
  // Estados para dados do usuário
  const [userProfile, setUserProfile] = useState<UserProfile>({
    id: '',
    name: '',
    email: '',
    familyName: '',
    createdAt: ''
  });

  // Sincronizar a aba ativa com a querystring (?tab=payment|profile|family)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const [editForm, setEditForm] = useState({
    name: userProfile.name,
    email: userProfile.email,
    familyName: userProfile.familyName
  });

  // Estados para membros da família
  const [members, setMembers] = useState<Member[]>([]);
  const [newMember, setNewMember] = useState({
    name: '',
    birthDate: '',
    gender: 'male' as 'male' | 'female',
    avatar: 'ri-user-line'
  });
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [showEditMember, setShowEditMember] = useState(false);
  const [showMemberUpgradeModal, setShowMemberUpgradeModal] = useState(false);
  const [memberFeatureUsage, setMemberFeatureUsage] = useState({ used: 0, limit: 3 });
  
  // Avatares: fallback e normalização de extensão
  const [brokenAvatars, setBrokenAvatars] = useState<Record<string, boolean>>({});
  const getAvatarSrc = useCallback((avatar?: string) => {
    if (!avatar || typeof avatar !== 'string') return '';
    if (avatar.includes('/Imagens/')) {
      try {
        const after = avatar.split('/Imagens/')[1] || '';
        const fileWithQuery = after.split('?')[0];
        const decoded = decodeURIComponent(fileWithQuery);
        const normalized = decoded.replace(/\.(png|jpg|jpeg|webp)$/i, '.webp');
        return `/Imagens/${normalized}`;
      } catch {
        if (avatar.startsWith('/Imagens/')) {
          return avatar.replace(/\.(png|jpg|jpeg|webp)(\?.*)?$/i, '.webp$2');
        }
      }
    }
    return avatar;
  }, []);

  const userSignature = useMemo(() => {
    if (!user) {
      return null;
    }

    const normalized = {
      id: user.id,
      fullName: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuário',
      email: user.email || '',
      createdAt: user.created_at || ''
    };

    return JSON.stringify(normalized);
  }, [user]);

  const currentUserSnapshot = useMemo(() => {
    if (!userSignature) {
      return null;
    }

    return JSON.parse(userSignature) as {
      id: string;
      fullName: string;
      email: string;
      createdAt: string;
    };
  }, [userSignature]);

  // Carregar dados do usuário e família
  useEffect(() => {
    let isCancelled = false;

    const loadProfileData = async () => {
      if (!currentUserSnapshot?.id) {
        if (isCancelled) return;
        setAverageAdherence(0);
        setMembers([]);
        setUserProfile({
          id: '',
          name: '',
          email: '',
          familyName: '',
          createdAt: ''
        });
        setLoading(false);
        return;
      }

      logger.debug('Carregando dados do perfil');
      setLoading(true);

      try {
        logger.sensitive('Usuário autenticado', { userId: currentUserSnapshot.id, email: currentUserSnapshot.email, fullName: currentUserSnapshot.fullName });

        const family = await familyService.getUserFamily();
        if (isCancelled) return;
        logger.sensitive('Família carregada', { familyId: family?.id, name: family?.name, created_by: family?.created_by });

        const familyMembers = await familyService.getUserFamilyMembers();
        if (isCancelled) return;
        logger.sensitive('Membros da família carregados', { count: familyMembers.length });

        setUserProfile({
          id: currentUserSnapshot.id,
          name: currentUserSnapshot.fullName,
          email: currentUserSnapshot.email,
          familyName: family?.name || 'Sem família',
          createdAt: currentUserSnapshot.createdAt
        });

        const formattedMembers: Member[] = familyMembers.map((member) => ({
          id: member.id || '',
          name: member.name,
          birthDate: member.birth_date || member.date_of_birth || '',
          gender: (member.gender as 'male' | 'female') || 'male',
          avatar: member.avatar_url || 'ri-user-line',
          status: 'good'
        }));

        setMembers(formattedMembers);
        setEditForm({
          name: currentUserSnapshot.fullName,
          email: currentUserSnapshot.email,
          familyName: family?.name || 'Sem família'
        });

        let calculatedAverage = 0;

        if (family?.id && formattedMembers.length > 0) {
          logger.sensitive('Calculando média de aderência para família', { familyId: family.id });

          const adherenceEntries = await adherenceService.getFamilyAdherence(family.id);
          if (isCancelled) return;
          const adherenceMap = new Map(
            adherenceEntries.map((entry) => [entry.member_id, entry.adherence_percentage ?? 0])
          );

          const totalPercentage = formattedMembers.reduce((sum, member) => {
            return sum + (adherenceMap.get(member.id) ?? 0);
          }, 0);

          calculatedAverage = Math.round(totalPercentage / formattedMembers.length);
        }

        if (isCancelled) return;
        setAverageAdherence(calculatedAverage);
      } catch (error) {
        console.error('❌ [ProfilePage] Erro ao carregar dados:', error);
        if (isCancelled) return;
        setAverageAdherence(0);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    void loadProfileData();

    return () => {
      isCancelled = true;
    };
  }, [currentUserSnapshot, familyService]);

  const handleSaveProfile = async () => {
    try {
      setLoading(true);

      if (!editForm.familyName.trim()) {
        toast.warning('Informe um nome válido para a família.');
        return;
      }

      const family = await familyService.getUserFamily();

      if (!family || !family.id) {
        toast.error('Erro: Família não encontrada para atualização.');
        return;
      }

      await familyService.updateFamily(family.id, {
        name: editForm.familyName
      });

      setUserProfile(prev => ({
        ...prev,
        name: editForm.name,
        email: editForm.email,
        familyName: editForm.familyName
      }));

      toast.success('Perfil atualizado com sucesso!');
      setIsEditing(false);
    } catch (error) {
      console.error('❌ [ProfilePage] Erro ao salvar perfil:', error);
      toast.error('Erro ao salvar as informações do perfil. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      name: userProfile.name,
      email: userProfile.email,
      familyName: userProfile.familyName
    });
    setIsEditing(false);
  };

  const handleAddMember = async () => {
    if (newMember.name && newMember.birthDate) {
      // ✅ VERIFICAR FEATURE GATE (limite de membros)
      if (user) {
        const access = await featureGateService.canUseFeature(user.id, 'family_member');
        
        if (!access.allowed) {
          // Limite atingido - mostrar modal de upgrade
          setMemberFeatureUsage({ 
            used: access.used || 0, 
            limit: access.limit || 3 
          });
          setShowMemberUpgradeModal(true);
          return;
        }
      }

      try {
        console.log('👤 [ProfilePage] Adicionando novo membro:', newMember);
        
        // Buscar família do usuário
        const family = await familyService.getUserFamily();
        
        if (!family) {
          toast.error('Erro: Família não encontrada');
          return;
        }
        
        // Adicionar membro no banco de dados
        const newMemberData = {
          name: newMember.name,
          birth_date: newMember.birthDate,
          gender: newMember.gender,
          avatar_url: newMember.avatar
        };
        
        await familyService.addFamilyMember(family.id || '', newMemberData as any);
        
        // Recarregar membros da família
        const familyMembers = await familyService.getUserFamilyMembers();
        const formattedMembers: Member[] = familyMembers.map((member) => ({
          id: member.id || '',
          name: member.name,
          birthDate: member.birth_date || member.date_of_birth || '',
          gender: (member.gender as 'male' | 'female') || 'male',
          avatar: member.avatar_url || 'ri-user-line',
          status: 'good'
        }));
        
        setMembers(formattedMembers);
        
        // Limpar formulário
        setNewMember({
          name: '',
          birthDate: '',
          gender: 'male',
          avatar: 'ri-user-line'
        });
        setShowAddMember(false);
        
        toast.success('Membro adicionado com sucesso!');
      } catch (error) {
        console.error('❌ [ProfilePage] Erro ao adicionar membro:', error);
        toast.error('Erro ao adicionar membro. Tente novamente.');
      }
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (window.confirm('Tem certeza que deseja remover este membro da família?')) {
      try {
        console.log('🗑️ [ProfilePage] Removendo membro:', memberId);
        
        await familyService.deleteFamilyMember(memberId);
        
        // Atualizar lista local
        setMembers(prev => prev.filter(member => member.id !== memberId));
        
        toast.success('Membro removido com sucesso!');
      } catch (error) {
        console.error('❌ [ProfilePage] Erro ao remover membro:', error);
        toast.error('Erro ao remover membro. Tente novamente.');
      }
    }
  };

  const handleGenderSelect = (gender: 'male' | 'female') => {
    if (showEditMember && editingMember) {
      setEditingMember((prev) => {
        if (!prev) return prev;
        const updatedAvatar = isImageAvatar(prev.avatar)
          ? prev.avatar
          : gender === 'male'
            ? 'ri-user-line'
            : 'ri-user-2-line';

        return {
          ...prev,
          gender,
          avatar: updatedAvatar
        };
      });
    } else {
      setNewMember(prev => ({
        ...prev,
        gender,
        avatar: gender === 'male' ? 'ri-user-line' : 'ri-user-2-line'
      }));
    }
    setShowIconModal(true);
  };

  const handleIconSelect = (icon: string) => {
    if (showEditMember && editingMember) {
      setEditingMember((prev) => prev ? ({ ...prev, avatar: icon }) : prev);
    } else {
      setNewMember(prev => ({
        ...prev,
        avatar: icon
      }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'bg-green-100 text-green-800 border-green-200';
      case 'good': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'excellent': return 'Excelente';
      case 'good': return 'Bom';
      case 'warning': return 'Atenção';
      case 'critical': return 'Crítico';
      default: return 'Normal';
    }
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'EXCLUIR') {
      toast.warning('Por favor, digite "EXCLUIR" para confirmar');
      return;
    }

    try {
      console.log('🗑️ [ProfilePage] Iniciando exclusão de conta...');
      await deleteAccount();
      console.log('✅ [ProfilePage] Conta excluída com sucesso!');
      navigate('/login');
    } catch (error) {
      console.error('❌ [ProfilePage] Erro ao excluir conta:', error);
      toast.error('Erro ao excluir conta. Por favor, tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-10 h-10 bg-gray-100 rounded-2xl flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <i className="ri-arrow-left-line text-gray-600 text-xl"></i>
            </button>
            
            <div className="text-center">
              <h1 className="text-xl font-bold text-gray-800">Meu Perfil</h1>
              <p className="text-sm text-gray-600">Gerencie suas informações</p>
            </div>
            
            <div className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center">
              <i className="ri-user-heart-fill text-emerald-600 text-xl"></i>
            </div>
          </div>
        </div>

        {/* Navegação por Tabs */}
        <div className="px-6">
          <div className="flex space-x-1 bg-gray-100 rounded-2xl p-1">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'profile'
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-user-line mr-2"></i>
              Perfil
            </button>
            <button
              onClick={() => setActiveTab('payment')}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'payment'
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-bank-card-line mr-2"></i>
              Pagamento
            </button>
            <button
              onClick={() => setActiveTab('family')}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'family'
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-group-line mr-2"></i>
              Família
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="px-6 py-6 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <i className="ri-loader-4-line animate-spin mr-2"></i>
            Carregando informações do perfil...
          </div>
        ) : (
          <>
        {/* Tab: Perfil */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Informações Básicas */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800">Informações Básicas</h2>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                  >
                    <i className="ri-edit-line mr-1"></i>
                    Editar
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCancelEdit}
                      className="text-gray-600 hover:text-gray-700 text-sm font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                    >
                      Salvar
                    </button>
                  </div>
                )}
              </div>

              {!isEditing ? (
                <div className="space-y-4">
                  <div className="flex items-center p-4 bg-gray-50 rounded-2xl">
                    <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mr-4">
                      <i className="ri-user-line text-emerald-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Nome</p>
                      <p className="font-medium text-gray-800">{userProfile.name}</p>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-gray-50 rounded-2xl">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mr-4">
                      <i className="ri-mail-line text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">E-mail</p>
                      <p className="font-medium text-gray-800">{userProfile.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-gray-50 rounded-2xl">
                    <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mr-4">
                      <i className="ri-home-heart-line text-purple-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Nome da Família</p>
                      <p className="font-medium text-gray-800">{userProfile.familyName}</p>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-gray-50 rounded-2xl">
                    <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mr-4">
                      <i className="ri-calendar-line text-green-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Membro desde</p>
                      <p className="font-medium text-gray-800">
                        {new Date(userProfile.createdAt).toLocaleDateString('pt-BR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  
                  {/* ID do usuário (8 primeiros caracteres do profiles.id) */}
                  <div className="flex items-center p-4 bg-gray-50 rounded-2xl">
                    <div className="w-12 h-12 bg-teal-100 rounded-2xl flex items-center justify-center mr-4">
                      <i className="ri-id-card-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">ID do usuário</p>
                      <p className="font-bold text-gray-800">
                        {userProfile.id ? userProfile.id.slice(0, 8) : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Input
                    label="Nome"
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Seu nome completo"
                  />
                  
                  <Input
                    label="E-mail"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="seu@email.com"
                  />
                  
                  <Input
                    label="Nome da Família"
                    value={editForm.familyName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, familyName: e.target.value }))}
                    placeholder="Ex: Família Silva"
                  />
                </div>
              )}
            </div>

            {/* Segurança */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800">Segurança e Privacidade</h2>
                <div className="w-10 h-10 bg-red-100 rounded-2xl flex items-center justify-center">
                  <i className="ri-shield-check-line text-red-600 text-xl"></i>
                </div>
              </div>

              <div className="space-y-4">
                {/* Senha */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center mr-4">
                      <i className="ri-lock-line text-orange-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Senha</p>
                      <p className="font-medium text-gray-800">••••••••••</p>
                      <p className="text-xs text-gray-500 mt-1">Protegida e criptografada</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/settings/change-password')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-2xl text-sm font-medium transition-colors flex items-center"
                  >
                    <i className="ri-key-line mr-2"></i>
                    Alterar Senha
                  </button>
                </div>

                {/* Exclusão de Conta */}
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-2xl border border-red-200">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mr-4">
                      <i className="ri-delete-bin-line text-red-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-red-600">Exclusão de Conta</p>
                      <p className="font-medium text-red-800">Ação irreversível</p>
                      <p className="text-xs text-red-500 mt-1">Exclua permanentemente sua conta e dados</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-2xl text-sm font-medium transition-colors flex items-center"
                  >
                    <i className="ri-error-warning-line mr-2"></i>
                    Excluir Conta
                  </button>
                </div>
              </div>
            </div>

            {/* Estatísticas */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Estatísticas</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-emerald-50 rounded-2xl">
                  <div className="text-2xl font-bold text-emerald-600">{members.length}</div>
                  <div className="text-sm text-emerald-700">Membros</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-2xl">
                  <div className="text-2xl font-bold text-blue-600">{`${averageAdherence}%`}</div>
                  <div className="text-sm text-blue-700">Aderência</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Pagamento */}
        {activeTab === 'payment' && (
          <div className="space-y-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <SubscriptionManager />
            </div>
          </div>
        )}

        {/* Tab: Família */}
        {activeTab === 'family' && (
          <div className="space-y-6">
            {/* Header da seção família */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Membros da Família</h2>
                <p className="text-sm text-gray-600">{members.length} {members.length === 1 ? 'membro' : 'membros'}</p>
              </div>
              <button
                onClick={() => setShowAddMember(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-2xl text-sm font-medium transition-colors"
              >
                <i className="ri-add-line mr-1"></i>
                Adicionar
              </button>
            </div>

            {/* Lista de membros */}
            <div className="space-y-4">
              {members.map((member) => (
                <div key={member.id} className="bg-white rounded-3xl p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mr-4 overflow-hidden">
                        {!brokenAvatars[member.id] && isImageAvatar(member.avatar) ? (
                          <img 
                            src={getAvatarSrc(member.avatar)} 
                            alt={`Avatar de ${member.name}`} 
                            className="w-full h-full object-cover"
                            onError={() => setBrokenAvatars(prev => ({ ...prev, [member.id]: true }))}
                          />
                        ) : (
                          <i className={`${member.avatar && member.avatar.startsWith('ri-') ? member.avatar : 'ri-user-line'} text-gray-500 text-2xl`}></i>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{member.name}</h3>
                        <p className="text-sm text-gray-600">
                          {calculateAge(member.birthDate)} anos • {member.gender === 'male' ? 'Masculino' : 'Feminino'}
                        </p>
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border mt-1 ${getStatusColor(member.status)}`}>
                          {getStatusText(member.status)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setEditingMember({ ...member });
                          setShowEditMember(true);
                        }}
                        className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 hover:bg-emerald-200 transition-colors"
                      >
                        <i className="ri-pencil-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => navigate(`/member/${member.id}`)}
                        className="w-10 h-10 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 hover:bg-blue-200 transition-colors"
                      >
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="w-10 h-10 bg-red-100 rounded-2xl flex items-center justify-center text-red-600 hover:bg-red-200 transition-colors"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Modal para adicionar membro */}
            {showAddMember && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
                <div className="bg-white rounded-3xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-800">Adicionar Membro</h3>
                    <button
                      onClick={() => setShowAddMember(false)}
                      className="w-8 h-8 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-600"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <Input
                      label="Nome"
                      value={newMember.name}
                      onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Nome do membro"
                    />
                    
                    <Input
                      label="Data de Nascimento"
                      type="date"
                      value={newMember.birthDate}
                      onChange={(e) => setNewMember(prev => ({ ...prev, birthDate: e.target.value }))}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sexo e Avatar</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => handleGenderSelect('male')}
                          className={`p-4 rounded-2xl border-2 transition-all ${
                            newMember.gender === 'male'
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-600'
                              : 'border-gray-200 text-gray-600'
                          }`}
                        >
                          <i className="ri-men-line text-2xl mb-2"></i>
                          <div className="text-sm font-medium">Masculino</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleGenderSelect('female')}
                          className={`p-4 rounded-2xl border-2 transition-all ${
                            newMember.gender === 'female'
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-600'
                              : 'border-gray-200 text-gray-600'
                          }`}
                        >
                          <i className="ri-women-line text-2xl mb-2"></i>
                          <div className="text-sm font-medium">Feminino</div>
                        </button>
                      </div>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <Button
                        onClick={() => setShowAddMember(false)}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleAddMember}
                        className="flex-1"
                        disabled={!newMember.name || !newMember.birthDate}
                      >
                        Adicionar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>

      {/* Modal de Seleção de Ícones */}
      <IconSelectorModal
        isOpen={showIconModal}
        onClose={() => setShowIconModal(false)}
        onSelectIcon={handleIconSelect}
        gender={showEditMember && editingMember ? editingMember.gender : newMember.gender}
        selectedIcon={showEditMember && editingMember ? editingMember.avatar : newMember.avatar}
        memberName={showEditMember && editingMember ? editingMember.name : newMember.name}
      />

      {/* Modal para editar membro */}
      {showEditMember && editingMember && createPortal(
        <div
          className={`fixed inset-0 bg-black flex items-center justify-center p-6 ${
            showIconModal
              ? 'bg-opacity-20 z-40 pointer-events-none'
              : 'bg-opacity-50 z-[60] pointer-events-auto'
          }`}
        >
          <div className="bg-white rounded-3xl p-6 w-full max-w-md relative">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">Editar Membro</h3>
              <button
                onClick={() => {
                  setShowEditMember(false);
                  setEditingMember(null);
                }}
                className="w-8 h-8 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-600"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Nome"
                value={editingMember.name}
                onChange={(e) => setEditingMember(prev => prev ? ({ ...prev, name: e.target.value }) : prev)}
                placeholder="Nome do membro"
              />

              <Input
                label="Data de Nascimento"
                type="date"
                value={editingMember.birthDate}
                onChange={(e) => setEditingMember(prev => prev ? ({ ...prev, birthDate: e.target.value }) : prev)}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sexo e Avatar</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleGenderSelect('male')}
                    className={`p-4 rounded-2xl border-2 transition-all ${
                      editingMember.gender === 'male'
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-600'
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    <i className="ri-men-line text-2xl mb-2"></i>
                    <div className="text-sm font-medium">Masculino</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGenderSelect('female')}
                    className={`p-4 rounded-2xl border-2 transition-all ${
                      editingMember.gender === 'female'
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-600'
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    <i className="ri-women-line text-2xl mb-2"></i>
                    <div className="text-sm font-medium">Feminino</div>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowIconModal(true)}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition-colors"
                >
                  <i className="ri-image-edit-line text-lg"></i>
                  Alterar ícone/foto
                </button>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={() => {
                    setShowEditMember(false);
                    setEditingMember(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={async () => {
                    if (!editingMember.name || !editingMember.birthDate) return;

                    try {
                      const updates = {
                        name: editingMember.name,
                        birth_date: editingMember.birthDate,
                        gender: editingMember.gender,
                        avatar_url: editingMember.avatar
                      };

                      await familyService.updateFamilyMember(editingMember.id, updates as any);

                      setMembers(prev => prev.map(member =>
                        member.id === editingMember.id
                          ? {
                              ...member,
                              name: editingMember.name,
                              birthDate: editingMember.birthDate,
                              gender: editingMember.gender,
                              avatar: editingMember.avatar
                            }
                          : member
                      ));

                      setShowEditMember(false);
                      setEditingMember(null);
                      toast.success('Membro atualizado com sucesso!');
                    } catch (error) {
                      console.error('❌ [ProfilePage] Erro ao atualizar membro:', error);
                      toast.error('Erro ao atualizar membro. Tente novamente.');
                    }
                  }}
                  className="flex-1"
                  disabled={!editingMember.name || !editingMember.birthDate}
                >
                  Salvar alterações
                </Button>
              </div>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Modal de Confirmação de Exclusão de Conta */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mr-3">
                  <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Excluir Conta</h3>
              </div>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                className="w-8 h-8 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-600"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                <p className="text-sm text-red-800 font-medium mb-2">⚠️ Atenção: Esta ação é irreversível!</p>
                <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                  <li>Todos os seus dados serão permanentemente excluídos</li>
                  <li>Sua família e membros serão removidos</li>
                  <li>Histórico de medicações será perdido</li>
                  <li>Pontos e conquistas serão apagados</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Para confirmar, digite <span className="font-bold text-red-600">EXCLUIR</span> abaixo:
                </label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Digite EXCLUIR"
                  className="text-center font-medium"
                />
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <Button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDeleteAccount}
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={deleteConfirmText !== 'EXCLUIR'}
                >
                  <i className="ri-delete-bin-line mr-2"></i>
                  Excluir Permanentemente
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal - Adicionar Membro */}
      {showMemberUpgradeModal && (
        <UpgradeModal
          featureKey="family_member"
          featureName="Membros da Família"
          currentPlan={subscription?.plan_tier || 'free'}
          usage={memberFeatureUsage}
          onClose={() => setShowMemberUpgradeModal(false)}
        />
      )}

      <BottomNavigation />
    </div>
  );
}



